import { useState } from "react";
import { BookOpen, Heart, Church, Bell, Award, ChevronRight, ChevronLeft, Sparkles, Users, X } from "lucide-react";

const onboardingSteps = [
  {
    id: "welcome",
    icon: Church,
    iconColor: "#118AB2",
    iconBg: "#E0F4FF",
    title: "Welcome to Your Church Family!",
    subtitle: "Let's take a quick tour",
    description: "You're now part of your church's Bible Buds community. Your family gets FREE access to all features through your church's subscription!",
    highlight: "No payment required - your church covers everything!"
  },
  {
    id: "children",
    icon: Users,
    iconColor: "#06D6A0",
    iconBg: "#E0FFF5",
    title: "Add Your Children",
    subtitle: "Personalized lessons for each child",
    description: "Add each of your children to get age-appropriate Bible lessons. We have three groups:",
    bullets: [
      { label: "Little Lights (3-7)", desc: "Simple stories, easy activities" },
      { label: "Bible Explorers (8-12)", desc: "Deeper lessons, fun challenges" },
      { label: "Faith Builders (13-18)", desc: "Mature topics, real-world faith" }
    ]
  },
  {
    id: "lessons",
    icon: BookOpen,
    iconColor: "#9B5DE5",
    iconBg: "#F0E6FF",
    title: "Daily Bible Lessons",
    subtitle: "New content every day",
    description: "Each child gets a fresh Bible lesson daily with:",
    bullets: [
      { label: "Bible Verse", desc: "Age-appropriate scripture" },
      { label: "Fun Explanation", desc: "Easy-to-understand teaching" },
      { label: "Activities", desc: "3 tasks to apply the lesson" },
      { label: "Quiz", desc: "Test understanding with fun questions" }
    ]
  },
  {
    id: "prayer",
    icon: Heart,
    iconColor: "#EF476F",
    iconBg: "#FFE4E6",
    title: "Prayer Requests",
    subtitle: "Share with your church family",
    description: "Submit prayer requests that your church pastor can see. Your church family will pray for you!",
    bullets: [
      { label: "Submit Requests", desc: "Share your prayer needs" },
      { label: "Track Status", desc: "See when prayers are answered" },
      { label: "Stay Connected", desc: "Build community through prayer" }
    ]
  },
  {
    id: "announcements",
    icon: Bell,
    iconColor: "#FFD166",
    iconBg: "#FFFBEB",
    title: "Church Announcements",
    subtitle: "Stay informed",
    description: "Your pastor can send announcements directly to your dashboard. Never miss important church updates!",
    highlight: "Look for the bell icon on your dashboard"
  },
  {
    id: "leaderboard",
    icon: Award,
    iconColor: "#F77F00",
    iconBg: "#FFF3E0",
    title: "Church Leaderboard",
    subtitle: "Friendly competition",
    description: "See how your children rank among other church families! Earn stars by completing lessons and activities.",
    highlight: "Encourage your kids to climb the leaderboard!"
  },
  {
    id: "ready",
    icon: Sparkles,
    iconColor: "#06D6A0",
    iconBg: "#E0FFF5",
    title: "You're All Set!",
    subtitle: "Start your Bible adventure",
    description: "Your family is ready to begin! Add your first child and start their Bible journey today.",
    highlight: "God bless your family's faith journey! 🙏"
  }
];

export default function ChurchFamilyOnboarding({ onComplete, churchName }) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    onComplete();
  };

  const StepIcon = step.icon;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
        {/* Progress bar */}
        <div className="h-1.5 bg-gray-100">
          <div 
            className="h-full transition-all duration-300"
            style={{ 
              width: `${((currentStep + 1) / onboardingSteps.length) * 100}%`,
              background: "linear-gradient(90deg, #06D6A0, #118AB2)"
            }}
          />
        </div>

        {/* Skip button */}
        <div className="flex justify-end p-3">
          <button 
            onClick={handleSkip}
            className="text-gray-400 hover:text-gray-600 text-sm font-medium flex items-center gap-1"
            data-testid="skip-onboarding"
          >
            Skip tour <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div 
              className="rounded-3xl p-5"
              style={{ background: step.iconBg }}
            >
              <StepIcon size={48} color={step.iconColor} strokeWidth={2} />
            </div>
          </div>

          {/* Church name badge (only on welcome) */}
          {step.id === "welcome" && churchName && (
            <div className="flex justify-center mb-3">
              <span className="bg-blue-100 text-blue-700 text-sm font-bold px-4 py-1.5 rounded-full">
                {churchName}
              </span>
            </div>
          )}

          {/* Title */}
          <h2 
            className="text-2xl font-black text-center text-[#073B4C] mb-1"
            style={{ fontFamily: "Fredoka, sans-serif" }}
          >
            {step.title}
          </h2>
          <p className="text-center text-gray-500 font-medium mb-4">
            {step.subtitle}
          </p>

          {/* Description */}
          <p className="text-center text-gray-600 mb-4 leading-relaxed">
            {step.description}
          </p>

          {/* Bullets (if any) */}
          {step.bullets && (
            <div className="space-y-2 mb-4">
              {step.bullets.map((bullet, idx) => (
                <div 
                  key={idx}
                  className="flex items-start gap-3 bg-gray-50 rounded-xl p-3"
                >
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: step.iconBg }}
                  >
                    <span className="text-xs font-black" style={{ color: step.iconColor }}>
                      {idx + 1}
                    </span>
                  </div>
                  <div>
                    <p className="font-bold text-[#073B4C] text-sm">{bullet.label}</p>
                    <p className="text-xs text-gray-500">{bullet.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Highlight (if any) */}
          {step.highlight && (
            <div 
              className="rounded-2xl p-4 text-center"
              style={{ background: step.iconBg }}
            >
              <p className="font-bold text-sm" style={{ color: step.iconColor }}>
                {step.highlight}
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <button
            onClick={handlePrev}
            disabled={isFirstStep}
            className={`flex items-center gap-1 font-bold py-2 px-4 rounded-xl transition-all ${
              isFirstStep 
                ? 'text-gray-300 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="onboarding-prev"
          >
            <ChevronLeft size={20} /> Back
          </button>

          {/* Step indicators */}
          <div className="flex gap-1.5">
            {onboardingSteps.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentStep 
                    ? 'w-6 bg-[#118AB2]' 
                    : idx < currentStep 
                      ? 'bg-[#06D6A0]' 
                      : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          <button
            onClick={handleNext}
            className="flex items-center gap-1 font-black py-2 px-4 rounded-xl text-white transition-all hover:opacity-90"
            style={{ 
              background: isLastStep 
                ? "linear-gradient(90deg, #06D6A0, #118AB2)" 
                : "#118AB2",
              fontFamily: "Fredoka, sans-serif"
            }}
            data-testid="onboarding-next"
          >
            {isLastStep ? "Get Started!" : "Next"} <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
